import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BRAND, BRAND_DARK, BRAND_LIGHT } from "@/shared/constants.js";
import { CheckIcon } from "@/shared/components/Icon.jsx";
import { MedMalGuardHeader } from "@/modules/Quote/components/MedMalGuardLanding.jsx";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { btnOutline, btnPrimary, dis } from "@/shared/utils/styles.js";
import { formatSubmissionListDate } from "@/shared/utils/format.js";
import { postGenerateBinderInvoice } from "@/modules/Payment/api/paymentApi.js";
import { fetchInsuredOrderDetails } from "@/modules/Quote/api/quoteApi.js";
import { readPolicyStatus, POLICY_STATUS } from "@/modules/Payment/utils/policyStatus.js";
import QuoteSnapshotRail from "@/modules/Quote/components/QuoteSnapshotRail.jsx";

import { useStore } from "@/shared/store/useStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import modalStore from "@/shared/store/modalStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";

export default function BinderInvoicePage() {
  const navigate = useNavigate();

  const flowSubmissionId = useStore(submissionStore, (s) => s.flowSubmissionId);
  const insuredProfile   = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated  = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);
  const sessionReady     = useStore(sessionStore, (s) => s.sessionReady);

  const [orderDetails, setOrderDetails] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [alreadyGenerated, setAlreadyGenerated] = useState(false);

  useEffect(() => {
    if (!flowSubmissionId) return undefined;
    let cancelled = false;
    setOrderLoading(true);
    setOrderError(null);
    fetchInsuredOrderDetails(flowSubmissionId)
      .then((data) => {
        if (cancelled) return;
        if (readPolicyStatus(data) !== POLICY_STATUS.PAID) { navigate("/dashboard"); return; }
        setOrderDetails(data);
      })
      .catch((e) => { if (!cancelled) setOrderError(e?.message || "Could not load submission details."); })
      .finally(() => { if (!cancelled) setOrderLoading(false); });
    return () => { cancelled = true; };
  }, [flowSubmissionId, navigate]);

  if (sessionReady && !isAuthenticated) return <Navigate to="/signin" replace />;

  const od = orderDetails;
  const r = od?.ratingResponse ?? null;

  const policyNum = od?.policynumber || od?.policyNumber || od?.quotenumber || od?.quoteNumber
    || (flowSubmissionId ? `#${flowSubmissionId}` : "—");
  const policyholder = od?.companyname
    || [od?.insuredfirstname, od?.insuredlastname].filter(Boolean).join(" ")
    || null;
  const email = od?.contactResponse?.email || null;
  const specialty = r?.specialtyTitle || od?.speciality || null;
  const limits = r?.coverageLimitTitle || null;
  const policyType = r?.currentprior || null;
  const eff = formatSubmissionListDate(r?.effectiveDate || od?.effectivedate);
  const exp = formatSubmissionListDate(r?.expirationDate || od?.expireddate);
  const totalVal = r?.total != null && Number.isFinite(Number(r.total))
    ? `$${Math.round(Number(r.total)).toLocaleString()}`
    : null;

  const handleGenerate = async () => {
    if (generating || !flowSubmissionId) return;
    setGenerateError(null);
    setGenerating(true);
    try {
      await postGenerateBinderInvoice(flowSubmissionId);
      sessionStore.bound = true;
      navigate("/complete-order");
    } catch (e) {
      if (e?.response?.status === 409) {
        setAlreadyGenerated(true);
        sessionStore.bound = true;
        return;
      }
      const msg = e?.response?.data?.message
        || e?.response?.data
        || e?.message
        || "Could not generate binder and invoice. Please try again.";
      setGenerateError(typeof msg === "string" ? msg : "Could not generate binder and invoice. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const summaryRows = [
    ["Submission", policyNum],
    policyholder && ["Policyholder", policyholder],
    email && ["Email", email],
    specialty && ["Specialty", specialty],
    limits && ["Limits", limits],
    policyType && ["Policy type", policyType],
    (eff && eff !== "—") && ["Effective", eff],
    (exp && exp !== "—") && ["Expiration", exp],
    totalVal && ["Total paid", totalVal],
  ].filter(Boolean);

  const renderContent = () => {
    if (!flowSubmissionId) {
      return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
          <p style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>No active submission found.</p>
          <button type="button" onClick={() => navigate("/dashboard")} style={{ ...btnPrimary, maxWidth: 240, fontSize: 13 }}>
            Go to Dashboard
          </button>
        </div>
      );
    }

    if (orderLoading) {
      return (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Loader label="Loading…" />
        </div>
      );
    }

    if (alreadyGenerated) {
      return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: BRAND_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
            <CheckIcon size={28} />
          </div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 600, color: BRAND_DARK, margin: "0 0 8px" }}>
            Already generated
          </h2>
          <p style={{ fontSize: 13, color: "#777", lineHeight: 1.6, maxWidth: 280, margin: "0 0 20px" }}>
            Your binder and invoice have already been generated for this submission.
          </p>
          <button type="button" onClick={() => navigate("/complete-order")} style={{ ...btnPrimary, maxWidth: 280, fontSize: 13 }}>
            View policy confirmation
          </button>
        </div>
      );
    }

    return (
      <>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", paddingTop: 24, marginBottom: 16 }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: BRAND_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
            <CheckIcon size={24} />
          </div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, color: BRAND_DARK, margin: "0 0 6px" }}>Payment successful</h2>
          <p style={{ fontSize: 13, color: "#777", lineHeight: 1.6, maxWidth: 300, margin: 0 }}>
            Your payment has been processed. Generate your binder and invoice to finalize your policy.
          </p>
        </div>

        <Alert type="error" message={orderError} />

        <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8 }}>Submission summary</div>
          {summaryRows.map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 11.5, color: "#888" }}>{l}</span>
              <span style={{ fontSize: 11.5, fontWeight: 500, color: "#444", textAlign: "right", maxWidth: "60%" }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ background: BRAND_LIGHT, borderRadius: 10, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 8 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2" strokeLinecap="round" style={{ width: 15, height: 15, flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span style={{ fontSize: 11, color: BRAND_DARK, lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif" }}>
            Clicking Generate will bind your policy and create your binder and invoice documents.
          </span>
        </div>

        <Alert type="error" message={generateError} className="alert-center" />

        <button
          type="button"
          style={dis({ ...btnPrimary, fontSize: 15 }, !generating)}
          onClick={handleGenerate}
        >
          {generating ? "Generating…" : "Generate Binder & Invoice"}
        </button>

        <button
          type="button"
          style={dis({ ...btnOutline, marginTop: 10, fontSize: 13 }, !generating)}
          onClick={() => navigate("/dashboard")}
          disabled={generating}
        >
          Go to Dashboard
        </button>
      </>
    );
  };

  return (
    <>
      {/* MedMalGuard header at every viewport — collapses to logo +
          hamburger on phones, same as the landing page. */}
      <div className="app-header">
        <MedMalGuardHeader />
      </div>

      <div style={{ flex: 1, padding: "0 18px 18px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {renderContent()}
      </div>

      <QuoteSnapshotRail />

      <div style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", textAlign: "center", fontSize: 10, color: "#ccc" }}>
        SelectFirst Insurance Services · (888) 966-3881 ·{" "}
        <span onClick={() => { modalStore.showAbout = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>About</span> ·{" "}
        <span onClick={() => { modalStore.showPrivacy = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Privacy</span> ·{" "}
        <span onClick={() => { modalStore.showTerms = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Terms</span>
      </div>
    </>
  );
}
